import useLabels from '../../hooks/useLabels';
import ChatWelcome from '../chat-welcome/ChatWelcome';
import ChatStarters from '../chat-starters/ChatStarters';
import ChatWindowHeader from '../chat-window-header/ChatWindowHeader';
import useInfo from '../../hooks/useInfo';
import LoadingIcon from '../../ui/loading-icon/LoadingIcon';
import Alert from '../../ui/alert/Alert';
import useEffectAsync from '../../hooks/useEffectAsync';
import JsonSchemaForm from '../json-schema-form';
import { Documents } from '../../ui/markdown-wrapper/hooks/useActions/actions/documents/Documents';
import { getClassNames } from '../../helpers/classHelpers';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppRoute, getChatItemRoute } from '../../router/Routing';
import { advancedAssistantKey, defaultPersonaKey, defaultTemperature } from '../../constants/consts';
import { useChat } from '../../contexts/chat/ChatContext';
import { ChatInput, ChatInputModel } from '../chat-input';
import { CHAT_INPUT_ERROR_ID } from '../chat-input/ChatInput';
import { validateInputModelSelection } from '../../views/chat-view/usePersistentInputState';
import { TestIds } from '../../mocks/ids';
import { ObjectTypes } from '../../ui/markdown-wrapper/hooks/useActions/useActions';
import { useErrorHandlerContext } from '../../contexts/error-handler/ErrorContext';
import { ChatViewLayoutProps } from '../../layouts/chat-view-layout/ChatViewLayout';
import { useAdaptive } from '../../contexts/adaptive/AdaptiveContext';
import { DocumentInfoModel } from '../../models/ChatTypes';
import { ChatInputVersionOptions } from '../chat-input/ChatInputOptions';
import { useLayout } from '../../contexts/layout/LayoutContext';
import { useAppInfo } from '../../contexts/app-info/AppInfoContext';
import { WorkflowOptions, ToolChoiceGroup } from '../../models/types';
import { isString } from '../../helpers/typeHelpers';
import style from './NewChatWindow.module.scss';
import { ObsoleteNotice } from '../../ui/obsolete-notice/ObsoleteNotice';
import Skeleton from '../../ui/skeleton/Skeleton';

const NewChatWindow: React.FC<ChatViewLayoutProps> = ({ onOpenSidebar, newPromptOrigin, onReloadChats }) => {
    const navigate = useNavigate();
    const { hash } = useLocation();
    const { catalogFavorites } = useAdaptive();
    const { getPersonaVersion } = useInfo();
    const { createChat, persistent } = useChat();
    const { llmOptions, allowedPersonaOptions } = useInfo();

    const { isRightSidebarVisible, isLeftSidebarVisible, hideLeftSidebar, collapseLeftSidebar, expandLeftSidebar } = useLayout();

    const { registerError, removeError } = useErrorHandlerContext();

    const promptOrigin = useMemo(() => newPromptOrigin!, [newPromptOrigin]);
    const defaultLlm = useMemo(() => llmOptions?.[0]?.value, [llmOptions]);
    const { getInput, setInput, mergeInput, setAutoSubmit, onInputChange, offInputChange } = persistent;



    const initialValues = useMemo(() => {
        const input = getInput(promptOrigin, {
            message: '',
            persona: promptOrigin
        });
        return input;
    }, [getInput, promptOrigin]);

    const [inputModel, setInputModel] = useState<ChatInputModel>(initialValues);
    // Refresh input model on persona change in initialValues
    useEffect(() => setInputModel(initialValues), [initialValues]);
    useEffect(() => {
        const handler = (model: ChatInputModel | undefined) => {
            // currently documents are updated outside of the input model state, so we need to capture changes to them.
            const documents = model?.documents ?? [];
            setInputModel(input => {
                if (!input.documents?.length && !documents?.length) {
                    return input;
                }
                const inputDocuments = input.documents ?? [];
                if (JSON.stringify(inputDocuments) === JSON.stringify(documents)) {
                    return input;
                }
                return { ...input, documents };
            });
        };
        onInputChange(promptOrigin, handler);
        return () => {
            offInputChange(promptOrigin, handler);
        };
    }, [onInputChange, offInputChange, promptOrigin]);

    const [isProcessingStarters, setIsProcessingStarters] = useState(false);

    const persona = useMemo(() => getPersonaVersion(inputModel.persona, inputModel.version, inputModel.build), [getPersonaVersion, inputModel.persona, inputModel.version, inputModel.build]);
    const personaDefaults = useMemo(() => {
        const { revision: { llm, temperature } = {} } = persona;
        return {
            llm,
            temperature
        };
    }, [persona]);

    const isDocumentUploadEnabled = useMemo(() => persona.revision?.documents?.enabled, [persona]);

    // Close side bar on persona without document upload feature
    useEffect(() => {
        if (isDocumentUploadEnabled && hash.includes(ObjectTypes.documents)) return;
        // navigate({ hash: '' }, { replace: true });
    }, [hash, isDocumentUploadEnabled, navigate]);

    const onModelMerge = useCallback((model: Partial<ChatInputModel>) => {
        setInputModel(m => ({ ...m, ...model }));
        mergeInput(promptOrigin, model);
    }, [mergeInput, promptOrigin]);

    // Validate input & correct invalid selections
    useEffect(() => {
        const { isValidSelection, isValidLlmSelection, isValidPersonaSelection } = validateInputModelSelection({ llm: inputModel.llm, persona: inputModel.persona }, llmOptions, allowedPersonaOptions);
        if (isValidSelection) return;
        if (!isValidLlmSelection) onModelMerge({ llm: defaultLlm });
        if (!isValidPersonaSelection) onModelMerge({ persona: catalogFavorites.firstFavorite });
    }, [inputModel.llm, inputModel.persona, llmOptions, allowedPersonaOptions, defaultLlm, onModelMerge, catalogFavorites.firstFavorite]);

    const labels = useLabels();
    const l = useMemo(() => {
        return {
            chatTitle: labels.newChatTitle,
            placeholder: labels.inputPlaceholder,
            disclaimerNote: labels.disclaimerNote,
        };
    }, [labels]);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const onSendPromptHandler = useCallback(async (alternativeMessage?: string, data?: Record<string, any>) => {

        const message = alternativeMessage ?? inputModel.message ?? '';

        const documents = isDocumentUploadEnabled ? inputModel.documents : undefined;

        let selectedPersona = persona.persona?.key;

        if (!selectedPersona)
            return;

        let version = persona.version?.version;
        let build = persona.revision?.build;
        setIsSubmitting(true);

        try {
            //console.log(documents);
            const { session_id, documents: responseDocuments } = await createChat(
                message,
                inputModel.llm || '',
                inputModel.temperature ?? defaultTemperature,
                selectedPersona,
                version,
                build,
                documents,
                inputModel.workflowInputs ?? data
            );

            // Remove the persisted input model of the new session and assign the message to the input model of the newly created session
            mergeInput(selectedPersona, {
                message: '',
                version,
                build,
                ...((session_id && isDocumentUploadEnabled) ? { documents: inputModel.documents?.filter(x => !responseDocuments?.includes(x)) } : {}),
                workflowInputs: inputModel.workflowInputs ?? data
            });
            setInput(session_id, { message, promptTemplateId: inputModel.promptTemplateId, documents });
            setAutoSubmit(session_id);

            catalogFavorites.setAsFavoriteOnInitialPrompt(inputModel.persona!);
            navigate({ pathname: getChatItemRoute(session_id), hash });
            onReloadChats?.(true);
        }
        finally {
            setIsSubmitting(false);
        }
    }, [inputModel, persona, isDocumentUploadEnabled, createChat, mergeInput, setInput, setAutoSubmit, catalogFavorites, navigate, hash, onReloadChats]);

    const onSelectStarter = useCallback(async (recommendation: string) => {
        removeError(CHAT_INPUT_ERROR_ID);

        try {
            setIsProcessingStarters(true);
            await onSendPromptHandler(recommendation);
        }
        catch (e: any) {
            registerError({ [CHAT_INPUT_ERROR_ID]: { type: 'modal', headline: 'errorHeadline', notification: { headline: labels.submitFailed, details: e } } });
        }
    }, [labels.submitFailed, onSendPromptHandler, registerError, removeError]);


    const currentPersona = useMemo(() => {
        return allowedPersonaOptions.find(x => x.key === inputModel.persona);
    }, [allowedPersonaOptions, inputModel.persona]);

    const [uploadedDocuments, setUploadedDocuments] = useState<DocumentInfoModel[]>();

    const { getPersonaVersionWorkflowOption } = useAppInfo();

    const [workflowOptions, setWorkflowOptions] = useState<WorkflowOptions | 'Failed' | 'Loading'>();
    const [workflowOptionsLoading, setWorkflowOptionsLoading] = useState(false);
    const workflowOptionsFor = useRef<string>();

    const workflowInputsRef = useRef<Record<string, any>>();
    const workflowInputs = useMemo(() => {
        if (!workflowOptions || isString(workflowOptions)) return {};
        // This is the initial workflow inputs, we do not want to change it after the first load
        if (workflowInputsRef.current) return workflowInputsRef.current;
        workflowInputsRef.current = { ...workflowOptions.values, ...initialValues.workflowInputs };
        return workflowInputsRef.current;
    }, [workflowOptions, initialValues]);

    useEffectAsync((token) => () => {
        const {
            persona: { key, type } = {},
            version: { version } = {},
            revision: { build, hasToolChoices } = {}
        } = persona;

        if (!key || !version || !build) return;
        if (!hasToolChoices && type !== 'workflow') {
            setWorkflowOptions(undefined);
            return;
        }

        const forPersona = [key, version, build].join("_");
        const silentReload = workflowOptionsFor.current && workflowOptionsFor.current === forPersona;

        workflowOptionsFor.current = forPersona;

        setWorkflowOptions(d => {
            // If the options already loaded, do the reload silently
            if (!silentReload || !d || isString(d)) {
                return 'Loading';
            }
            return d;
        });
        setWorkflowOptionsLoading(true);

        getPersonaVersionWorkflowOption(key, version, build, inputModel.workflowInputs)
            .then(options => {
                if (token.cancelled)
                    return;
                setWorkflowOptions(options);
            })
            .catch(error => {

                setWorkflowOptions(d => {
                    if (!silentReload || !d || isString(d)) {
                        return 'Failed';
                    }
                    return d;
                });

                console.warn('Failed to load workflow options', error);
            })
            .finally(() => {
                setWorkflowOptionsLoading(false);
            });

    }, [getPersonaVersionWorkflowOption, persona, inputModel.workflowInputs]);

    const [choices, setChoices] = useState<ToolChoiceGroup[]>();
    useEffect(() => {
        if (!workflowOptions || isString(workflowOptions))
            setChoices([]);
        else
            setChoices(workflowOptions.choices ?? []);
    }, [workflowOptions]);

    const handleToggleChoice = useCallback((checked: boolean, groupKey: string, choiceKey: string, valueKey: string) => {
        const choicesData = inputModel?.workflowInputs ?? {};
        const group = choicesData[groupKey] ?? {};
        const choiceValues = group[choiceKey] ?? [];
        const newChoiceValues = checked ? [...choiceValues, valueKey] : choiceValues.filter((v: string) => v !== valueKey);
        const newChoicesData = {
            ...choicesData,
            [groupKey]: {
                ...group,
                [choiceKey]: newChoiceValues
            }
        };
        onModelMerge({ workflowInputs: newChoicesData });
    }, [onModelMerge, inputModel]);
    return (<>
        <div data-testid={TestIds.chatWindow} className={getClassNames([style['chat-window']])}>

            <ChatWindowHeader
                initialTitle={l.chatTitle}
                isLeftSidebarVisible={isLeftSidebarVisible}
                isRightSidebarVisible={isRightSidebarVisible}
                onBurgerClick={onOpenSidebar}
                expandLeftSidebar={() => {
                    expandLeftSidebar();
                    // showLeftSidebar();
                }}
                collapseLeftSidebar={() => {
                    collapseLeftSidebar();
                    hideLeftSidebar();
                }}
            />
            <div className={style['chat-window-wrapper']}>
                <div className={style['new-chat-messages']}>
                    <ChatWelcome persona={currentPersona} />
                    {currentPersona?.key === advancedAssistantKey && (
                        <ObsoleteNotice
                            message={labels.advancedObsoleteDescription}
                            buttonLabel={labels.basicChatAssistantTitle}
                            onClick={() => {
                                catalogFavorites.swapPinnedPersonas?.(advancedAssistantKey, defaultPersonaKey);
                                navigate(AppRoute.basicAssistant);
                            }}
                        />
                    )}
                    <ChatStarters
                        className={style.starters}
                        isDisabled={isProcessingStarters}
                        starters={currentPersona?.starters}
                        onSubmit={onSelectStarter}
                        onPrefill={message => onModelMerge({ message })}
                    />
                </div>

                {currentPersona?.type !== 'workflow'
                    ? <div className={style['input-container']}>
                        <SessionToolChoices choices={choices} inputModel={inputModel} handleToggleChoice={handleToggleChoice} loading={workflowOptionsLoading} showLoading={workflowOptions === 'Loading'} />
                        <ChatInput
                            displayNewChatOptions
                            model={inputModel}
                            defaultModel={personaDefaults}
                            placeholder={l.placeholder}
                            inputNote={l.disclaimerNote}

                            personaOptions={allowedPersonaOptions}
                            llmOptions={llmOptions}
                            onModelMerge={onModelMerge}
                            onSubmitPrompt={onSendPromptHandler}
                        />
                    </div>
                    : <div className={getClassNames([style['input-container'], style['workflow-input']])}>
                        <ChatInputVersionOptions persona={inputModel.persona} version={inputModel.version} build={inputModel.build} onModelMerge={onModelMerge} llmOptions={llmOptions} personaOptions={allowedPersonaOptions} isInputDisabled={false} />
                        <br />
                        <Documents showInfo={false} reportUploadStateChange={setUploadedDocuments} chatId='' showDescriptionCta={false} />
                        {workflowOptions &&
                            <>
                                {
                                    isString(workflowOptions)
                                        ? workflowOptions === 'Loading' ? <div className={style['loading-indicator']}><LoadingIcon /></div> : <Alert type='error' headline={labels.workflowOptionsLoadFailed} />
                                        : <JsonSchemaForm schema={workflowOptions.form?.form_schema ?? {}} ui={workflowOptions.form?.form_ui ?? {}} initialFormData={workflowInputs} submitLabel={!isSubmitting ? labels.submit : labels.submitting} disabledSubmit={isSubmitting || !uploadedDocuments?.length} onSubmit={options => onSendPromptHandler(uploadedDocuments?.[0].display.name ?? "Workflow", options)} />
                                }
                            </>
                        }
                    </div>
                }
            </div>
        </div>
    </>);
};

export default NewChatWindow;



const SessionToolChoices = ({ choices, inputModel, handleToggleChoice, loading, showLoading }: { choices?: ToolChoiceGroup[], inputModel: ChatInputModel, handleToggleChoice: (checked: boolean, groupKey: string, choiceKey: string, valueKey: string) => void; loading: boolean; showLoading: boolean; }) => {

    return <div style={{ maxWidth: 800, margin: '0 auto' }}>
        {
            loading && showLoading ? <>
                <Skeleton type='form' rows={2} optionsPerRow={4} />
                <br />
            </> :
                choices?.map(group => (
                    <div key={group.key}>
                        <div>
                            <b>{group.name}</b>
                            {group.description && <div><i><small>{group.description}</small></i></div>}
                        </div>
                        <div style={{ paddingLeft: 6 }}>
                            {group.choices.filter(choice => choice.values?.length).map(choice => {
                                const groupState = inputModel?.workflowInputs?.[group.key]?.[choice.key] ?? [];
                                const implicitAllSelected = !groupState.length;
                                return (
                                    <div key={choice.key} style={{ borderTop: '1px solid #eee', paddingTop: 8, paddingBottom: 6 }}>
                                        <div><span>{choice.name}</span> {implicitAllSelected ? <i style={{ opacity: .5 }}> - All</i> : null}</div>
                                        <div style={{ display: 'flex', gap: 7 }}>
                                            {
                                                choice.values.map(value => (
                                                    <div key={value.key}>
                                                        <label><input type="checkbox" checked={groupState.includes(value.key)} onChange={e => handleToggleChoice(e.target.checked, group.key, choice.key, value.key)} />{value.name}</label>
                                                    </div>
                                                ))
                                            }
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <div style={{ borderBottom: '1px solid #eee', marginBottom: 15 }}>
                            {group.summary && <div className={loading ? style.pulse : ''}><i><b><small>{group.summary}</small></b></i></div>}
                        </div>

                    </div>
                ))
        }
    </div>;
};